import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect,
} from "react";
import "./style/CaseLine.css";
import StarMini from "../assets/image/Star.svg"
import StarTrio from "../assets/image/star-trio.svg"
import { ReactComponent as StarBackground } from "../assets/image/case-item-background.svg"

import CaseOpenSound from "../assets/sound/case.mp3"

const CaseLine = forwardRef(({ min_bounty, max_bounty, base_bounty, onWin, needSound = false, baseCaseColor }, ref) => {
  const [items, setItems] = useState([]);
  const reelRef = useRef(null);
  const animationRef = useRef(null);
  const positionRef = useRef(0);
  const itemWidthRef = useRef(70);
  const [isRolling, setIsRolling] = useState(false);

  const audioRef = useRef(null);
  const lastPlayedRef = useRef(new Set());
  const lastPlayTimeRef = useRef(0);

  const BASE_LENGTH = 50;

  const generateItems = () =>
    Array.from({ length: BASE_LENGTH }, () => {
      const value =
        Math.floor(Math.random() * (max_bounty - min_bounty + 1)) + min_bounty;

      return {
        img: value >= base_bounty * 2.5 ? StarTrio : StarMini,
        value,
      };
    });


  useEffect(() => {
    const base = generateItems();
    setItems(base);

    const id = setTimeout(() => {
      if (reelRef.current?.children[0]) {
        itemWidthRef.current =
          reelRef.current.children[0].getBoundingClientRect().width;
      }
    }, 30);

    return () => clearTimeout(id);
  }, [min_bounty, max_bounty]);

  const easeInOutSmooth = (t) =>
    t < 0.5
      ? Math.pow(2 * t, 3) / 2
      : 1 - Math.pow(2 * (1 - t), 3) / 2;

  useImperativeHandle(ref, () => ({
    roll: (speed = 1, targetValue = null) => {
      if (isRolling) return;
      setIsRolling(true);
      cancelAnimationFrame(animationRef.current);

      const winnerValue = targetValue ?? Math.floor(Math.random() * (max_bounty - min_bounty + 1)) + min_bounty;

      const winner = {
        img: winnerValue >= base_bounty * 2.5 ? StarTrio : StarMini,
        value: winnerValue,
      };

      const filler = Array.from({ length: 8 }, () => {
        const value =
          Math.floor(Math.random() * (max_bounty - min_bounty + 1)) + min_bounty;
        return {
          img: value >= base_bounty * 2.5 ? StarTrio : StarMini,
          value,
        };
      });

      const newItems = [...items, winner, ...filler];
      setItems(newItems);
      lastPlayedRef.current.clear();

      setTimeout(() => {
        const start = performance.now();
        const duration = 4000 / speed;
        const startPos = positionRef.current;

        const winnerIndex = newItems.length - filler.length;
        const winnerOffset = Math.random() * 30 - 15;
        const wrapperWidth = reelRef.current.parentElement.offsetWidth;
        const itemWidth = itemWidthRef.current;

        const maxShift = reelRef.current.scrollWidth - wrapperWidth;
        const rawTarget =
          -winnerIndex * itemWidth + wrapperWidth / 2 - itemWidth / 2 + winnerOffset;
        const targetPos = Math.min(0, Math.max(-maxShift, rawTarget));

        const animate = (now) => {
          if (!reelRef.current) {
            setIsRolling(false);
            return;
          }

          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeInOutSmooth(progress);

          positionRef.current = startPos + (targetPos - startPos) * eased;
          reelRef.current.style.transform = `translateX(${positionRef.current}px)`;

          if (needSound && audioRef.current && localStorage.getItem("audio") === "1") {
            const reelChildren = Array.from(reelRef.current.children);
            const reelCenter = wrapperWidth / 2;

            reelChildren.forEach((child, idx) => {
              const childCenter = child.offsetLeft + itemWidth / 2 + positionRef.current;
              if (
                Math.abs(childCenter - reelCenter) < itemWidth / 2 &&
                !lastPlayedRef.current.has(idx)
              ) {
                const now = performance.now();
                if (now - lastPlayTimeRef.current > 90) { // 90 мс между звуками
                    audioRef.current.currentTime = 0;
                    audioRef.current.play().catch(() => {});
                    lastPlayTimeRef.current = now;
                    lastPlayedRef.current.add(idx);
                }
              }
            });
          }

          if (progress < 1) {
            animationRef.current = requestAnimationFrame(animate); // TODO: возможно стоит убрать!!! для оптимизацию под мобилки, и оставить просто translate
          } else {
            positionRef.current = targetPos;
            reelRef.current.style.transform = `translateX(${targetPos}px)`;
            setIsRolling(false);

            if (onWin) onWin(winner.value);
          }
        };

        animationRef.current = requestAnimationFrame(animate);
      }, 20);
    },
  }));

  return (
    <div className="case-line-wrapper">
      <div className="reel-container">
        <div className="case-line" ref={reelRef}>
          {items.map((item, idx) => {
            const color =
              item.value >= base_bounty * 2.5
                ? "rgba(239, 168, 14, 1)"
                : baseCaseColor;

            const imgSize =
              item.value >= base_bounty * 2.5
                ? { width: 104, height: 64 }
                : { width: 66, height: 64 };

            return (
              <div className="case-item" key={idx}>
                <div
                  className="background-layer"
                  style={{ color }}
                >
                  <StarBackground />
                </div>

                <img src={item.img} alt="" width={imgSize.width} height={imgSize.height} />
                <span>{item.value}</span>
                <div className="down" style={{ background: color }}></div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="reel-center-line"></div>
      {needSound && localStorage.getItem("audio") === "1" && <audio ref={audioRef} src={CaseOpenSound} preload="auto" />}
    </div>
  );
});

export default CaseLine;
